import { useSyncExternalStore } from 'react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './toast';

type ToastVariant = 'default' | 'destructive' | 'success';
interface ToastItem {
  id: number;
  title?: string;
  description?: string;
  variant?: ToastVariant;
}

let nextId = 1;
let items: ToastItem[] = [];
const subscribers = new Set<() => void>();

function emit(): void {
  for (const fn of subscribers) fn();
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

function getSnapshot(): ToastItem[] {
  return items;
}

/** Imperative toast API. Use anywhere; fully decoupled from React tree. */
export const toast = {
  show(t: Omit<ToastItem, 'id'>): number {
    const id = nextId++;
    items = [...items, { id, ...t }];
    emit();
    setTimeout(() => toast.dismiss(id), 5000);
    return id;
  },
  success(title: string, description?: string): number {
    return toast.show({ title, description, variant: 'success' });
  },
  error(title: string, description?: string): number {
    return toast.show({ title, description, variant: 'destructive' });
  },
  dismiss(id: number): void {
    items = items.filter((it) => it.id !== id);
    emit();
  },
};

/** Mount once near the app root. */
export function Toaster() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <ToastProvider>
      {list.map((t) => (
        <Toast key={t.id} variant={t.variant} onOpenChange={(open) => !open && toast.dismiss(t.id)}>
          <div className="grid gap-1">
            {t.title ? <ToastTitle>{t.title}</ToastTitle> : null}
            {t.description ? <ToastDescription>{t.description}</ToastDescription> : null}
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
